/**
 * Tech Panel - UI for technology research with graphical tech tree
 * Features: branch tabs, canvas tree view, detail panel with research button
 */

import { useGameStore } from '@/state/gameStore';
import type { TechSystem } from '@/game/tech/TechSystem';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { FactionId } from '@/types';
import type { Tech, TechBranch, TechId, TechStatus } from '@/game/tech/TechTypes';
import { TECH_BRANCHES, BRANCH_ORDER, getBranchColor } from '@/game/tech/TechBranches';
import { getTech } from '@/game/tech/data';
import { TechTreeCanvas } from './TechTreeCanvas';

export class TechPanel {
  private container: HTMLElement;
  private techSystem: TechSystem | null = null;
  private factionManager: FactionManager | null = null;
  private visible: boolean = false;
  private selectedBranch: TechBranch = 'bingfa';
  private selectedTech: Tech | null = null;

  // Sub-components
  private techCanvas: TechTreeCanvas | null = null;
  private canvasContainer: HTMLElement | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'tech-panel';
    this.container.innerHTML = `
      <style>
        #tech-panel {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 200;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
          width: 1200px;
          max-width: 95vw;
          aspect-ratio: 2 / 1;
          display: none;
        }
        #tech-panel.visible {
          display: block;
        }
        /* Frame overlay - on top of content */
        .tech-panel-frame {
          position: absolute;
          inset: 0;
          background: url('/images/ui/tech-panel/panel-frame-16-9.webp') center / 100% 100% no-repeat;
          pointer-events: none;
          z-index: 10;
        }
        .tech-panel-wrapper {
          position: absolute;
          /* Fill frame inner area */
          top: 10%;
          left: 4%;
          right: 4%;
          bottom: 6%;
          /* Inner padding to keep content away from frame edges */
          padding: 8px 16px 4px 16px;
          background: url('/images/ui/tech-panel/panel-bg.webp') center center / cover no-repeat;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          z-index: 1;
          box-sizing: border-box;
        }
        .tech-panel-title-bar {
          background: linear-gradient(180deg, #6b4423 0%, #4a2f17 100%);
          border-bottom: 2px solid #8b6914;
          padding: 8px 12px;
          padding-top: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .tech-panel-title {
          font: 700 14px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }
        .tech-panel-title-info {
          font-size: 11px;
          color: #c4a77d;
          margin-left: 6px;
        }
        .tech-panel-body {
          display: flex;
          flex: 1;
          min-height: 0;
        }
        .tech-panel-left {
          flex: 1;
          display: flex;
          flex-direction: column;
          border-right: 2px solid rgba(139,105,20,0.5);
          padding-left: 24px;
        }
        .tech-panel-right {
          width: 280px;
          background: rgba(0,0,0,0.2);
          display: flex;
          flex-direction: column;
          padding-right: 24px;
          padding-bottom: 24px;
        }
        #tech-panel .branch-tabs {
          display: flex;
          background: rgba(0,0,0,0.2);
          border-bottom: 2px solid rgba(139,105,20,0.5);
          overflow-x: auto;
        }
        #tech-panel .branch-tab {
          flex: 1;
          min-width: 50px;
          padding: 5px 4px;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: #9e8b6e;
          cursor: pointer;
          font: 10px 'Noto Serif SC', 'SimSun', serif;
          transition: all 0.15s ease;
          text-align: center;
        }
        #tech-panel .branch-tab:hover {
          color: #f5e6d3;
          background: rgba(255,255,255,0.05);
        }
        #tech-panel .branch-tab.active {
          color: #fff;
          background: rgba(255,255,255,0.08);
        }
        #tech-panel .branch-icon {
          display: block;
          font-size: 12px;
          margin-bottom: 1px;
        }
        #tech-panel .current-research {
          padding: 8px 12px;
          background: linear-gradient(180deg, rgba(46,93,50,0.8) 0%, rgba(27,74,32,0.8) 100%);
          border-bottom: 2px solid #4caf50;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        #tech-panel .current-research.hidden {
          display: none;
        }
        #tech-panel .research-info {
          flex: 1;
        }
        #tech-panel .research-title {
          color: #c8e6c9;
          font-weight: 600;
          font-size: 12px;
        }
        #tech-panel .research-progress-bar {
          height: 5px;
          background: rgba(0,0,0,0.3);
          border-radius: 3px;
          margin-top: 4px;
          overflow: hidden;
        }
        #tech-panel .research-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
          transition: width 0.3s ease;
        }
        #tech-panel .research-percentage {
          color: #8bc34a;
          font-size: 11px;
          font-weight: 600;
        }
        #tech-panel .speedup-btn {
          padding: 4px 10px;
          background: linear-gradient(180deg, #d4af37 0%, #b8962f 100%);
          border: 1px solid #ffd35a;
          border-radius: 4px;
          color: #2d1f15;
          font: 600 11px 'Noto Serif SC', 'SimSun', serif;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }
        #tech-panel .speedup-btn:hover {
          background: linear-gradient(180deg, #e5c048 0%, #c9a73a 100%);
          box-shadow: 0 0 8px rgba(212,175,55,0.5);
        }
        #tech-panel .speedup-btn:disabled {
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border-color: #6b4e0f;
          color: #9e8b6e;
          cursor: not-allowed;
          box-shadow: none;
        }
        .tech-canvas-container {
          flex: 1;
          background: transparent;
          position: relative;
          min-height: 0;
        }
        .zoom-controls {
          position: absolute;
          bottom: 8px;
          right: 8px;
          display: flex;
          gap: 4px;
          z-index: 10;
        }
        .zoom-btn {
          width: 28px;
          height: 28px;
          background: rgba(45,31,21,0.9);
          border: 2px solid #8b6914;
          border-radius: 4px;
          color: #f5e6d3;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }
        .zoom-btn:hover {
          background: rgba(74,55,40,0.95);
          border-color: #d4af37;
        }
        .tech-legend {
          padding: 4px 8px;
          background: rgba(0,0,0,0.15);
          display: flex;
          justify-content: center;
          gap: 10px;
          font-size: 8px;
          color: #888;
          border-top: 1px solid rgba(139,105,20,0.3);
          padding-bottom: 24px;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 3px;
        }
        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 2px solid;
        }
        .legend-dot.completed { border-color: #d4af37; background: #4a4020; }
        .legend-dot.researching { border-color: #4fc3f7; background: #1e3a5f; }
        .legend-dot.available { border-color: #81c784; background: #2d4a2d; }
        .legend-dot.locked { border-color: #616161; background: #3a3a3a; }
        .legend-dot.excluded { border-color: #c62828; background: #4a2020; }

        /* Detail Panel */
        .detail-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .detail-header {
          padding: 12px;
          background: linear-gradient(180deg, rgba(60,45,30,0.9) 0%, rgba(40,28,18,0.9) 100%);
          border-bottom: 2px solid #8b6914;
          text-align: center;
        }
        .detail-header-icon {
          width: 72px;
          height: 72px;
          margin: 0 auto 6px;
          border-radius: 50%;
          border: 3px solid #8b6914;
          background-color: #2a2a2a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          overflow: hidden;
          position: relative;
        }
        .detail-header-icon.has-sprite {
          font-size: 0;
        }
        .detail-header-icon canvas {
          width: 100%;
          height: 100%;
        }
        .detail-header-name {
          font: 700 15px 'Noto Serif SC', serif;
          color: #ffd35a;
        }
        .detail-header-tier {
          font-size: 11px;
          color: #c4a77d;
          margin-top: 2px;
        }
        .detail-content {
          flex: 1;
          padding: 12px;
          overflow-y: auto;
        }
        .detail-section {
          margin-bottom: 12px;
        }
        .detail-section-title {
          font-size: 11px;
          color: #8b6914;
          text-transform: uppercase;
          margin-bottom: 6px;
          border-bottom: 1px solid rgba(139,105,20,0.3);
          padding-bottom: 3px;
        }
        .detail-description {
          font-size: 12px;
          color: #d4c4a8;
          line-height: 1.5;
        }
        .detail-quote {
          font-style: italic;
          color: #9e8b6e;
          font-size: 11px;
          margin-top: 6px;
          padding-left: 8px;
          border-left: 2px solid #8b6914;
        }
        .detail-effects {
          font-size: 11px;
        }
        .detail-effect {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
          color: #a5d6a7;
        }
        .detail-prereqs {
          font-size: 11px;
        }
        .detail-prereq {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 3px 0;
        }
        .detail-prereq-status {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .detail-prereq-status.done { background: #4caf50; }
        .detail-prereq-status.pending { background: #f44336; }
        .detail-cost {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 6px 0;
        }
        .detail-cost-value {
          color: #ffd700;
          font-weight: 600;
        }
        .detail-footer {
          padding: 12px;
          border-top: 2px solid rgba(139,105,20,0.5);
          background: rgba(0,0,0,0.2);
        }
        .detail-status {
          text-align: center;
          font-size: 12px;
          margin-bottom: 10px;
          padding: 6px;
          border-radius: 4px;
        }
        .detail-status.completed {
          background: rgba(212,175,55,0.2);
          color: #ffd35a;
        }
        .detail-status.researching {
          background: rgba(33,150,243,0.2);
          color: #64b5f6;
        }
        .detail-status.available {
          background: rgba(76,175,80,0.2);
          color: #81c784;
        }
        .detail-status.locked {
          background: rgba(97,97,97,0.2);
          color: #9e9e9e;
        }
        .detail-status.excluded {
          background: rgba(198,40,40,0.2);
          color: #ef5350;
        }

        /* Research Button */
        .sprite-btn {
          position: relative;
          width: 100%;
          height: 44px;
          background: url('/images/ui/tech-panel/btn-enabled.webp') center / 100% 100% no-repeat;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font: 700 14px 'Noto Serif SC', serif;
          color: #f5e6d3;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          transition: filter 0.15s, transform 0.1s;
        }
        .sprite-btn:hover {
          filter: brightness(1.1);
        }
        .sprite-btn:active {
          transform: scale(0.98);
          filter: brightness(0.95);
        }
        .sprite-btn:disabled {
          background-image: url('/images/ui/tech-panel/btn-disabled.webp');
          cursor: not-allowed;
          color: #666;
          filter: none;
        }

        .detail-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #666;
          font-size: 12px;
        }
        .detail-empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
          opacity: 0.5;
        }
      </style>
      <div class="tech-panel-frame"></div>
      <div class="tech-panel-wrapper">
        <div class="tech-panel-title-bar">
          <span class="tech-panel-title">科技树</span>
          <span class="tech-panel-title-info" id="tech-count-info"></span>
        </div>
        <div class="tech-panel-body">
          <div class="tech-panel-left">
            <div class="current-research hidden">
              <div class="research-info">
                <div class="research-title">正在研究...</div>
                <div class="research-progress-bar"><div class="research-progress-fill"></div></div>
              </div>
              <div class="research-percentage">0%</div>
              <button class="speedup-btn">加速</button>
            </div>
            <div class="branch-tabs"></div>
            <div class="tech-canvas-container">
              <div class="zoom-controls">
                <button class="zoom-btn" id="zoom-in">+</button>
                <button class="zoom-btn" id="zoom-out">−</button>
                <button class="zoom-btn" id="zoom-reset">⌂</button>
              </div>
            </div>
            <div class="tech-legend">
              <div class="legend-item"><div class="legend-dot completed"></div>已完成</div>
              <div class="legend-item"><div class="legend-dot researching"></div>研究中</div>
              <div class="legend-item"><div class="legend-dot available"></div>可研究</div>
              <div class="legend-item"><div class="legend-dot locked"></div>未解锁</div>
              <div class="legend-item"><div class="legend-dot excluded"></div>互斥</div>
            </div>
          </div>
          <div class="tech-panel-right">
            <div class="detail-panel" id="detail-panel">
              <div class="detail-empty">
                <div class="detail-empty-icon">📜</div>
                <div>点击科技查看详情</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupEvents();
    this.renderBranchTabs();
  }

  setTechSystem(system: TechSystem): void {
    this.techSystem = system;
  }

  setFactionManager(manager: FactionManager): void {
    this.factionManager = manager;
  }

  private setupEvents(): void {

    const zoomIn = this.container.querySelector('#zoom-in');
    const zoomOut = this.container.querySelector('#zoom-out');
    const zoomReset = this.container.querySelector('#zoom-reset');

    zoomIn?.addEventListener('click', () => this.techCanvas?.zoomIn());
    zoomOut?.addEventListener('click', () => this.techCanvas?.zoomOut());
    zoomReset?.addEventListener('click', () => this.techCanvas?.resetView());

    // Speed up research button
    const speedupBtn = this.container.querySelector('.speedup-btn');
    speedupBtn?.addEventListener('click', () => this.handleSpeedUpClick());

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.visible) {
        if (this.selectedTech) {
          this.selectedTech = null;
          this.renderDetailPanel();
        } else {
          this.hide();
        }
        e.stopPropagation();
        e.preventDefault();
      }
    }, true);
  }

  private renderBranchTabs(): void {
    const tabsContainer = this.container.querySelector('.branch-tabs');
    if (!tabsContainer) return;

    const branchIcons: Record<TechBranch, string> = {
      bingfa: '⚔',
      baigong: '🔨',
      nonggeng: '🌾',
      shangdao: '💰',
      moxue: '⚙',
      yinyang: '☯',
      faction: '🏴',
      ultimate: '🐉',
    };

    tabsContainer.innerHTML = BRANCH_ORDER.map(branch => {
      const config = TECH_BRANCHES[branch];
      return `
        <button class="branch-tab ${branch === this.selectedBranch ? 'active' : ''}"
                data-branch="${branch}"
                style="border-bottom-color: ${branch === this.selectedBranch ? config.color : 'transparent'}">
          <span class="branch-icon">${branchIcons[branch]}</span>
          ${config.shortName}
        </button>
      `;
    }).join('');

    const tabs = tabsContainer.querySelectorAll('.branch-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.selectedBranch = (tab as HTMLElement).dataset.branch as TechBranch;
        this.selectedTech = null;
        this.renderBranchTabs();
        this.updateCanvas();
        this.renderDetailPanel();
      });
    });
  }

  show(): void {
    this.visible = true;
    this.container.classList.add('visible');
    this.initCanvas();
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.container.classList.remove('visible');
    this.selectedTech = null;
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  private initCanvas(): void {
    if (this.techCanvas) return;

    this.canvasContainer = this.container.querySelector('.tech-canvas-container');
    if (!this.canvasContainer) return;

    const rect = this.canvasContainer.getBoundingClientRect();
    this.techCanvas = new TechTreeCanvas(this.canvasContainer, {
      width: rect.width || 700,
      height: rect.height || 400,
      onTechClick: (techId) => this.handleTechSelect(techId),
      onTechHover: () => {}, // No tooltip, we use detail panel
    });

    this.updateCanvas();
  }

  private updateCanvas(): void {
    if (!this.techCanvas || !this.techSystem) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    let techs: Tech[];
    if (this.selectedBranch === 'faction') {
      techs = this.techSystem.getTechsForBranch(playerFaction, 'faction');
    } else {
      techs = this.techSystem.getTechsForBranch(playerFaction, this.selectedBranch);
    }

    const statuses = new Map<TechId, TechStatus>();
    for (const tech of techs) {
      statuses.set(tech.id, this.techSystem.getTechStatus(playerFaction, tech.id));
    }

    this.techCanvas.setTechs(techs, statuses);
  }

  private handleTechSelect(techId: TechId): void {
    const tech = getTech(techId);
    if (tech) {
      this.selectedTech = tech;
      this.renderDetailPanel();
    }
  }

  private handleResearchClick(): void {
    if (!this.selectedTech || !this.techSystem) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const canResult = this.techSystem.canResearch(playerFaction, this.selectedTech.id);
    if (canResult.can) {
      if (this.techSystem.startResearch(playerFaction, this.selectedTech.id)) {
        useGameStore.getState().addMessage(`开始研究: ${this.selectedTech.name}`);
        this.render();
      }
    } else {
      useGameStore.getState().addMessage(canResult.reason || '无法研究');
    }
  }

  private handleSpeedUpClick(): void {
    if (!this.techSystem) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const current = this.techSystem.getCurrentResearch(playerFaction);
    if (!current) return;

    const cost = this.techSystem.getSpeedUpCost(playerFaction);
    const gold = this.factionManager?.getGold(playerFaction) || 0;

    if (gold < cost) {
      useGameStore.getState().addMessage('金币不足，无法加速');
      return;
    }

    if (this.techSystem.speedUpResearch(playerFaction)) {
      const tech = getTech(current.techId);
      useGameStore.getState().addMessage(`研究加速完成: ${tech?.name || current.techId}`);
      this.render();
    }
  }

  private renderDetailPanel(): void {
    const panel = this.container.querySelector('#detail-panel');
    if (!panel) return;

    if (!this.selectedTech) {
      panel.innerHTML = `
        <div class="detail-empty">
          <div class="detail-empty-icon">📜</div>
          <div>点击科技查看详情</div>
        </div>
      `;
      return;
    }

    const tech = this.selectedTech;
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction || !this.techSystem) return;

    const status = this.techSystem.getTechStatus(playerFaction, tech.id);
    const researched = new Set(this.techSystem.getResearchedTechs(playerFaction));
    const canResearch = this.techSystem.canResearch(playerFaction, tech.id);

    // Status text
    const statusTexts: Record<TechStatus, string> = {
      completed: '✓ 已完成',
      researching: '⟳ 研究中',
      available: '◉ 可研究',
      locked: '🔒 未解锁',
      excluded: '✗ 互斥锁定',
    };

    // Format effects
    let effectsHtml = '';
    if (tech.effects?.bonuses) {
      const bonusNames: Record<string, string> = {
        // 战斗加成
        attackBonus: '攻击加成',
        defenseBonus: '防御加成',
        speedBonus: '移动速度',
        unitHealthBonus: '单位血量',
        morale: '士气加成',
        // 兵种攻击
        swordAttack: '剑士攻击',
        spearAttack: '枪兵攻击',
        cavalryAttack: '骑兵攻击',
        cavalryCharge: '骑兵冲锋',
        archerAttack: '弓兵攻击',
        archerRange: '弓箭射程',
        // 防御
        shieldDefense: '盾兵防御',
        shieldBlock: '盾牌格挡',
        // 道士
        taoistPower: '道术威力',
        taoistHeal: '治疗效果',
        // 经济
        incomeBonus: '收入加成',
        farmIncome: '农业收入',
        mineIncome: '矿业收入',
        tradingBonus: '贸易加成',
        marketBonus: '市场加成',
        taxBonus: '税收加成',
        goldCapacity: '金币容量',
        upkeepReduction: '维护费减少',
        // 生产
        buildSpeedBonus: '建造速度',
        trainSpeedBonus: '训练速度',
        researchSpeedBonus: '研究速度',
        repairSpeed: '修复速度',
        // 地形
        forestBonus: '林地加成',
        hillBonus: '山地加成',
        // 其他
        siegeBonus: '攻城加成',
      };

      for (const [key, value] of Object.entries(tech.effects.bonuses)) {
        const name = bonusNames[key] || key;
        let displayValue = '';
        if (typeof value === 'number') {
          if (value > 1 && value < 10) {
            displayValue = `+${Math.round((value - 1) * 100)}%`;
          } else if (value < 1) {
            displayValue = `-${Math.round((1 - value) * 100)}%`;
          } else {
            displayValue = `+${value}`;
          }
        }
        effectsHtml += `<div class="detail-effect"><span>${name}</span><span>${displayValue}</span></div>`;
      }
    }

    // Format prerequisites
    let prereqsHtml = '';
    for (const prereqId of tech.prerequisites) {
      const prereqTech = getTech(prereqId);
      const isDone = researched.has(prereqId);
      prereqsHtml += `
        <div class="detail-prereq">
          <div class="detail-prereq-status ${isDone ? 'done' : 'pending'}"></div>
          <span>${prereqTech?.name || prereqId}</span>
        </div>
      `;
    }

    // Button state
    const canClick = status === 'available' && canResearch.can;
    const buttonText = status === 'completed' ? '已完成'
      : status === 'researching' ? '研究中...'
      : status === 'available' ? '开始研究'
      : status === 'excluded' ? '互斥锁定'
      : '未解锁';

    // Get icon URL
    const iconUrl = this.getTechIconUrl(tech);

    panel.innerHTML = `
      <div class="detail-header">
        <div class="detail-header-icon ${iconUrl ? 'has-icon' : ''}" id="detail-icon-container">
          ${iconUrl ? '' : (tech.icon || '?')}
        </div>
        <div class="detail-header-name">${tech.name}</div>
        <div class="detail-header-tier">第${tech.tier}层 · ${getBranchColor(tech.branch) ? TECH_BRANCHES[tech.branch].name : tech.branch}</div>
      </div>
      <div class="detail-content">
        <div class="detail-section">
          <div class="detail-section-title">描述</div>
          <div class="detail-description">${tech.description}</div>
          ${tech.quote ? `<div class="detail-quote">"${tech.quote}"</div>` : ''}
        </div>
        ${effectsHtml ? `
          <div class="detail-section">
            <div class="detail-section-title">效果</div>
            <div class="detail-effects">${effectsHtml}</div>
          </div>
        ` : ''}
        ${prereqsHtml ? `
          <div class="detail-section">
            <div class="detail-section-title">前置科技</div>
            <div class="detail-prereqs">${prereqsHtml}</div>
          </div>
        ` : ''}
        <div class="detail-section">
          <div class="detail-section-title">花费</div>
          <div class="detail-cost">
            <span>金币</span>
            <span class="detail-cost-value">${tech.cost}</span>
          </div>
          <div class="detail-cost">
            <span>研究时间</span>
            <span class="detail-cost-value">${tech.researchTime}分钟</span>
          </div>
        </div>
      </div>
      <div class="detail-footer">
        <div class="detail-status ${status}">${statusTexts[status]}</div>
        <button class="sprite-btn" id="research-btn" ${!canClick ? 'disabled' : ''}>
          ${buttonText}
        </button>
      </div>
    `;

    // Add click handler for research button
    const researchBtn = panel.querySelector('#research-btn');
    researchBtn?.addEventListener('click', () => this.handleResearchClick());

    // Draw icon if available
    if (iconUrl) {
      this.drawDetailIcon(iconUrl);
    }
  }

  private drawDetailIcon(url: string): void {
    const container = this.container.querySelector('#detail-icon-container');
    if (!container) return;

    const canvas = document.createElement('canvas');
    canvas.width = 72;
    canvas.height = 72;
    container.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Draw circular clipped icon
      ctx.beginPath();
      ctx.arc(36, 36, 34, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, 0, 0, 72, 72);
    };
    img.src = url;
  }

  render(): void {
    if (!this.techSystem || !this.factionManager || !this.visible) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    this.renderCurrentResearch(playerFaction);
    this.updateCanvas();
    this.renderDetailPanel();
    this.updateStats(playerFaction);
  }

  /**
   * Lightweight update for progress bar (called from game loop)
   */
  update(): void {
    if (!this.visible || !this.techSystem) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const current = this.techSystem.getCurrentResearch(playerFaction);
    const currentEl = this.container.querySelector('.current-research') as HTMLElement;

    // If no current research, hide the progress bar and do a full refresh
    if (!current) {
      if (currentEl && !currentEl.classList.contains('hidden')) {
        this.render(); // Full refresh when research completed
      }
      return;
    }

    const fillEl = this.container.querySelector('.research-progress-fill') as HTMLElement;
    const percentEl = this.container.querySelector('.research-percentage');
    const percent = Math.floor(current.progress * 100);

    if (fillEl) fillEl.style.width = `${percent}%`;
    if (percentEl) percentEl.textContent = `${percent}%`;

    // Check if research completed
    if (current.progress >= 1) {
      this.render(); // Full refresh when completed
    }
  }

  private renderCurrentResearch(factionId: FactionId): void {
    const currentEl = this.container.querySelector('.current-research') as HTMLElement;
    const current = this.techSystem!.getCurrentResearch(factionId);

    if (!current) {
      currentEl.classList.add('hidden');
      return;
    }

    currentEl.classList.remove('hidden');
    const titleEl = currentEl.querySelector('.research-title');
    const fillEl = currentEl.querySelector('.research-progress-fill') as HTMLElement;
    const percentEl = currentEl.querySelector('.research-percentage');
    const speedupBtn = currentEl.querySelector('.speedup-btn') as HTMLButtonElement;

    const tech = getTech(current.techId);
    const percent = Math.floor(current.progress * 100);

    if (titleEl) titleEl.textContent = `正在研究: ${tech?.name || current.techId}`;
    if (fillEl) fillEl.style.width = `${percent}%`;
    if (percentEl) percentEl.textContent = `${percent}%`;

    // Update speedup button
    if (speedupBtn) {
      const cost = this.techSystem!.getSpeedUpCost(factionId);
      const gold = this.factionManager?.getGold(factionId) || 0;
      speedupBtn.textContent = `加速 (${cost}金)`;
      speedupBtn.disabled = gold < cost;
    }
  }

  private updateStats(factionId: FactionId): void {
    const researchedCount = this.techSystem!.getResearchedCount(factionId);
    const available = this.techSystem!.getAvailableTechs(factionId);
    const infoEl = this.container.querySelector('#tech-count-info');
    if (infoEl) infoEl.textContent = `(${researchedCount}/${available.length})`;
  }

  private getTechIconUrl(tech: Tech): string | null {
    // Ultimate tech
    if (tech.id === 'ULTIMATE') {
      return '/images/tech/ultimate.webp';
    }

    // Faction techs: F_HAN_001 -> /images/tech/han/H1.webp
    const factionMatch = tech.id.match(/^F_([A-Z]+)_(\d+)$/);
    if (factionMatch) {
      const [, faction, numStr] = factionMatch;
      const num = parseInt(numStr, 10);
      const factionLower = faction.toLowerCase();
      const prefix = faction.charAt(0);
      return `/images/tech/${factionLower}/${prefix}${num}.webp`;
    }

    // Get branch from tech ID
    let branch: string | null = null;
    if (tech.id.startsWith('B')) branch = 'bingfa';
    else if (tech.id.startsWith('G')) branch = 'baigong';
    else if (tech.id.startsWith('N')) branch = 'nonggeng';
    else if (tech.id.startsWith('S')) branch = 'shangdao';
    else if (tech.id.startsWith('M')) branch = 'moxue';
    else if (tech.id.startsWith('Y')) branch = 'yinyang';

    if (!branch) return null;

    // Parse tech number
    const match = tech.id.match(/^[A-Z](\d+)$/);
    if (!match) return null;

    const num = parseInt(match[1], 10);

    // Calculate icon number (1-18)
    let iconNum = 0;
    if (num >= 41) iconNum = 17 + (num - 41);
    else if (num >= 31) iconNum = 14 + (num - 31);
    else if (num >= 21) iconNum = 9 + (num - 21);
    else if (num >= 11) iconNum = 4 + (num - 11);
    else iconNum = num;

    return `/images/tech/${branch}/${tech.id.charAt(0)}${iconNum}.webp`;
  }

  dispose(): void {
    this.techCanvas?.dispose();
    this.container.remove();
  }
}
